import { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Tag } from "primereact/tag";
import { colorPairs, formatAmount, formatDate } from "../../utils/functions";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import KpiCard from "../../components/features/user/KpiCard";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { TransactionFiltersDTO, TransactionsDto, TransactionStatusEnum } from "../../models/Transaction";
import { transactionsService } from "../../services/transactionsService";
import { customerService } from "../../services/customerService";
import Loader from "../../components/common/Loader";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useNavigate } from "react-router-dom";
import { payoutService, PayoutTransactionStatusEnum } from "../../services/payoutService";

const AdminDashboard = () => {
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [topCustomer, setTopCustomer] = useState<any[]>([]);
  const [recentPayoutTxs, setRecentPayoutTxs] = useState<any[]>([]);
  const [payoutTotalAccounts, setPayoutTotalAccounts] = useState<number>(0);

  const toastRef = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  const [transactionModalVisible, setTransactionModalVisible] = useState(false);
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  const showToast = (severity: "success" | "error", summary: string, detail: string) => {
    toastRef.current?.show({ severity, summary, detail, life: 3000 });
  };

  const [data, setData] = useState({
    numTransaction: null,
    successRate: null,
    totalCollected: { totalAmount: "", currency: "" },
    topServices: [{ serviceId: "", serviceName: "", transactionCount: 0 }],
    totalCustomer: { total: 0 },
    allTransactions: null,
    chartData: {},
    chartOptions: {},
  });

  const userFilters: TransactionFiltersDTO = { page: 1, limit: 5 };

  const fetchAllTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await transactionsService.getAllTransactions(userFilters);
      setAllTransactions(response.data);
    } catch {
      toastRef.current?.show({
        severity: "warn",
        summary: "Error",
        detail: "Error while fetching payin transactions",
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopCustomer = async () => {
    try {
      const response = await transactionsService.getTopCustomers();
      setTopCustomer(response.data);
    } catch {
      // non-blocking
    }
  };

  const fetchTransactionDetails = async (transactionId: string) => {
    try {
      const transaction = await transactionsService.transactionDetailsAdmin(transactionId);
      setSelectedTransaction(transaction.data);
      setTransactionModalVisible(true);
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch transaction details",
        life: 3000,
      });
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [
        totalTransactionsRes,
        successRateRes,
        totalCollectedRes,
        totalCustomerRes,
        topServicesRes,
      ] = await Promise.all([
        transactionsService.getTotalTransactions(),
        transactionsService.adminGetSuccessRate(),
        transactionsService.getTotalAmount(),
        customerService.getTotalCustomer(),
        transactionsService.adminTopCustomers(),
      ]);

      if (
        totalTransactionsRes.status === -1 ||
        successRateRes.status === -1 ||
        totalCollectedRes.status === -1 ||
        totalCustomerRes.status === -1 ||
        topServicesRes.status === -1
      ) {
        throw new Error("One or more API calls failed");
      }

      setData((prev) => ({
        ...prev,
        numTransaction: totalTransactionsRes.data.totalTransactions,
        successRate: successRateRes.data.successRate,
        totalCollected: totalCollectedRes.data,
        totalCustomer: totalCustomerRes.data,
        topServices: topServicesRes.data,
      }));
    } catch (error: any) {
      showToast("error", "Error", error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPayoutData = async () => {
    try {
      setPayoutLoading(true);
      const [txRes, accountsRes] = await Promise.all([
        payoutService.getAllTransactions({ page: 1, limit: 5 }),
        payoutService.getAllAccounts({ page: 1, limit: 1 }),
      ]);
      if (txRes?.data?.data) setRecentPayoutTxs(txRes.data.data);
      else if (Array.isArray(txRes?.data)) setRecentPayoutTxs(txRes.data);
      if (accountsRes?.data?.total != null) setPayoutTotalAccounts(accountsRes.data.total);
    } catch {
      // non-blocking
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleViewTransaction = (transactionId: string) => {
    fetchTransactionDetails(transactionId);
  };

  useEffect(() => {
    if (currentUser) {
      fetchData();
      fetchAllTransactions();
      fetchTopCustomer();
      fetchPayoutData();
    }
  }, [currentUser]);

  const payinSeverity = (status: TransactionStatusEnum): "success" | "info" | "warning" | "danger" => {
    switch (status) {
      case TransactionStatusEnum.SUCCESS:  return "success";
      case TransactionStatusEnum.INITIATED: return "info";
      case TransactionStatusEnum.PENDING:  return "warning";
      case TransactionStatusEnum.FAILED:   return "danger";
      default: return "info";
    }
  };

  const payoutSeverity = (status: PayoutTransactionStatusEnum): "success" | "info" | "warning" | "danger" => {
    switch (status) {
      case PayoutTransactionStatusEnum.SUCCESS:    return "success";
      case PayoutTransactionStatusEnum.PROCESSING: return "info";
      case PayoutTransactionStatusEnum.PENDING:    return "warning";
      case PayoutTransactionStatusEnum.FAILED:
      case PayoutTransactionStatusEnum.CANCELLED:  return "danger";
      default: return "info";
    }
  };

  const chartData = {
    labels: topCustomer.map((user) => user.customerName),
    datasets: [
      {
        label: "Amount",
        backgroundColor: topCustomer.map((_, index) => {
          const colors = ["rgba(54, 162, 235, 0.2)"];
          return colors[index % colors.length];
        }),
        data: topCustomer.map((user) => user.totalAmount),
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" } },
  };

  const payinKpis = [
    {
      title: "Total Customers",
      amount: data?.totalCustomer?.total,
      icon: "pi pi-users",
    },
    {
      title: "Success Rate",
      amount: `${data.successRate ?? 0}%`,
      icon: "pi pi-check-circle",
    },
    {
      title: "Total Collected",
      amount: `${formatAmount(data?.totalCollected?.totalAmount)} ${data?.totalCollected?.currency ?? ""}`,
      icon: "pi pi-wallet",
    },
    {
      title: "Total Transactions",
      amount: data.numTransaction,
      icon: "pi pi-history",
    },
  ];

  const payoutKpis = [
    {
      title: "Payout Accounts",
      amount: payoutTotalAccounts,
      icon: "pi pi-users",
    },
    {
      title: "Recent Payouts",
      amount: recentPayoutTxs.length,
      icon: "pi pi-send",
    },
    {
      title: "Payout Success",
      amount: recentPayoutTxs.length
        ? `${Math.round(
            (recentPayoutTxs.filter((t) => t.status === PayoutTransactionStatusEnum.SUCCESS).length /
              recentPayoutTxs.length) *
              100
          )}%`
        : "N/A",
      icon: "pi pi-check-circle",
    },
    {
      title: "Payout Volume",
      amount: recentPayoutTxs.length
        ? `${formatAmount(
            recentPayoutTxs.reduce((sum, t) => sum + Number(t.amount ?? 0), 0)
          )} ${recentPayoutTxs[0]?.currency ?? "XAF"}`
        : "N/A",
      icon: "pi pi-wallet",
    },
  ];

  return (
    <div className="admin-dashboard">
      <Toast ref={toastRef} />
      {isLoading ? (
        <div className="load">
          <Loader />
        </div>
      ) : (
        <>
          {/* Payin KPIs */}
          <div className="bg-white rounded-md p-4 mb-4">
            <div className="flex items-center gap-2 mb-4 text-sm text-secondary-500">
              <i className="pi pi-arrow-down-left text-blue-500"></i>
              <p className="m-0 font-semibold text-blue-500">Payin — Key Indicators</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {payinKpis.map((item, index) => {
                const colors = colorPairs[index % colorPairs.length];
                return item.amount != null ? (
                  <KpiCard key={index} colors={colors} item={item} />
                ) : (
                  <div key={index} className="text-center text-gray-400 text-sm">N/A</div>
                );
              })}
            </div>
          </div>

          {/* Payout KPIs */}
          <div className="bg-white rounded-md p-4 mb-4">
            <div className="flex items-center gap-2 mb-4 text-sm text-secondary-500">
              <i className="pi pi-arrow-up-right text-emerald-500"></i>
              <p className="m-0 font-semibold text-emerald-500">Payout — Key Indicators</p>
            </div>
            {payoutLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {payoutKpis.map((item, index) => {
                  const colors = [...colorPairs].reverse()[index % colorPairs.length];
                  return (
                    <KpiCard key={index} colors={colors} item={item} />
                  );
                })}
              </div>
            )}
          </div>

          {/* Chart + Top Services */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div className="bg-white p-4 rounded-md">
              <p className="m-0 text-sm text-secondary-500 mb-4">Users with most transactions</p>
              {topCustomer.length > 0 ? (
                <Chart
                  type="bar"
                  data={chartData}
                  options={chartOptions}
                  style={{ height: "400px" }}
                />
              ) : (
                <div className="text-center text-gray-400 py-8">No data available</div>
              )}
            </div>
            <div className="bg-white rounded-md p-4">
              <div className="flex items-center gap-2 mb-4 text-sm text-secondary-500">
                <i className="pi pi-objects-column"></i>
                <p className="m-0">Top Services</p>
              </div>
              {data.topServices.map((service, index) => {
                const colors = [...colorPairs].reverse()[index % colorPairs.length];
                return (
                  <div
                    key={service.serviceId ?? index}
                    className="flex items-center gap-2 bg-white rounded-md p-3 border mb-3"
                  >
                    <div className={`h-2 w-2 rounded-full ${colors.bg}`}></div>
                    <div className={`border-l-2 ${colors.bd} rounded-s-md w-full`}>
                      <div className="pl-4 flex flex-col">
                        <span className="font-semibold text-xs text-black">
                          {service.serviceName ?? "---"}
                        </span>
                        <span className="text-gray-600 text-xs">
                          Successful transactions: {service.transactionCount ?? "0"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Transactions — Payin + Payout side by side */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Payin transactions */}
            <div className="flex-1 min-w-0 bg-white p-4 rounded-md shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-secondary-500">
                  <i className="pi pi-arrow-down-left text-blue-500"></i>
                  <p className="m-0 font-semibold">Recent Payin Transactions</p>
                </div>
                <Button
                  label="View All"
                  className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 text-sm"
                  onClick={() => navigate("/admin/transaction-history")}
                />
              </div>
              <DataTable
                value={Array.isArray(allTransactions) ? allTransactions.slice(0, 5) : []}
                tableClassName="text-sm"
                filterDisplay="menu"
              >
                <Column field="trans_code" header="Code" sortable />
                <Column field="customer_msisdn" header="Customer" sortable />
                <Column
                  field="amount"
                  header="Amount"
                  body={(rowData) => (
                    <span>{rowData.amount} {rowData.currency}</span>
                  )}
                  sortable
                />
                {/*<Column*/}
                {/*  field="commission"*/}
                {/*  header="Commission"*/}
                {/*  body={(rowData) => (*/}
                {/*    <span>{rowData.commission} {rowData.currency}</span>*/}
                {/*  )}*/}
                {/*  sortable*/}
                {/*/>*/}
                <Column
                  field="status"
                  header="Status"
                  body={(rowData: TransactionsDto) => (
                    <Tag
                      value={rowData.status}
                      severity={payinSeverity(rowData.status as TransactionStatusEnum)}
                    />
                  )}
                  sortable
                />
                <Column
                    header="Date"
                    body={(rowData) =>
                        new Date(rowData.createdAt).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                    }
                    sortable/>
                <Column
                  header="Actions"
                  body={(rowData) => (
                    <Button
                      label="Details"
                      className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 text-xs"
                      onClick={() => handleViewTransaction(rowData.id)}
                    />
                  )}
                />
              </DataTable>
            </div>

            {/* Payout transactions */}
            <div className="flex-1 min-w-0 bg-white p-4 rounded-md shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-secondary-500">
                  <i className="pi pi-arrow-up-right text-emerald-500"></i>
                  <p className="m-0 font-semibold">Recent Payout Transactions</p>
                </div>
                <Button
                  label="View All"
                  className="bg-emerald-500 text-white px-2 py-1 rounded hover:bg-emerald-600 text-sm"
                  onClick={() => navigate("/admin/payout")}
                />
              </div>
              {payoutLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : (
                <DataTable
                  value={recentPayoutTxs}
                  tableClassName="text-sm"
                  emptyMessage="No payout transactions"
                >
                  <Column field="transCode" header="Code" sortable />
                  <Column field="recipientMsisdn" header="Recipient" sortable />
                  <Column
                    field="amount"
                    header="Amount"
                    body={(rowData) => (
                      <span>{formatAmount(rowData.amount)} {rowData.currency ?? "XAF"}</span>
                    )}
                    sortable
                  />
                  <Column
                    field="status"
                    header="Status"
                    body={(rowData) => (
                      <Tag
                        value={rowData.status}
                        severity={payoutSeverity(rowData.status as PayoutTransactionStatusEnum)}
                      />
                    )}
                    sortable
                  />
                  <Column
                    field="createdAt"
                    header="Date"
                    body={(rowData) => formatDate(rowData.createdAt)}
                    sortable
                  />
                </DataTable>
              )}
            </div>
          </div>

          {/* Payin Transaction Detail Modal */}
          <Dialog
            header="Transaction Details"
            visible={transactionModalVisible}
            closeIcon="pi pi-times"
            className="p-fluid bg-white rounded-xl shadow-lg max-w-3xl w-full"
            onHide={() => setTransactionModalVisible(false)}
          >
            {selectedTransaction ? (
              <div className="p-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-4">
                  <div>
                    <div className="text-xs text-gray-500">Transaction</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {selectedTransaction.trans_code}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(selectedTransaction.createdAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag
                      value={selectedTransaction.status}
                      severity={payinSeverity(selectedTransaction.status as TransactionStatusEnum)}
                      className="px-3 py-1 text-xs uppercase"
                    />
                    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs text-gray-600">
                      <i className="pi pi-globe" />
                      {selectedTransaction.countryCode?.toUpperCase()}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs text-gray-600">
                      <i className="pi pi-desktop" />
                      {selectedTransaction.isTest ? "Test" : "Live"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-lg border p-4">
                    <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="pi pi-user" />
                      Customer Info
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-500">Name</div>
                      <div className="text-right text-gray-800">{selectedTransaction.customer_name}</div>
                      <div className="text-gray-500">Email</div>
                      <div className="text-right text-gray-800">{selectedTransaction.customer_email ?? "N/A"}</div>
                      <div className="text-gray-500">Phone</div>
                      <div className="text-right text-gray-800">{selectedTransaction.customer_msisdn}</div>
                      <div className="text-gray-500">Type</div>
                      <div className="text-right">
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                          <i className="pi pi-briefcase" />
                          {selectedTransaction.customer?.isEntreprise ? "Enterprise" : "Customer"}
                        </span>
                      </div>
                      {selectedTransaction.customer?.isEntreprise && (
                        <>
                          <div className="text-gray-500">Enterprise</div>
                          <div className="text-right text-gray-800">
                            {selectedTransaction.customer?.entreprise_name ?? "N/A"}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="pi pi-link" />
                      References
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-500">Reference ID</div>
                      <div className="text-right text-gray-800">{selectedTransaction.referenceId ?? "N/A"}</div>
                      <div className="text-gray-500">External ID</div>
                      <div className="text-right text-gray-800">{selectedTransaction.externalTransactionId ?? "N/A"}</div>
                      <div className="text-gray-500">Service</div>
                      <div className="text-right text-gray-800">
                        {selectedTransaction.subscription?.partnerService?.service?.name ?? "N/A"}
                      </div>
                      <div className="text-gray-500">Partner</div>
                      <div className="text-right text-gray-800">
                        {selectedTransaction.subscription?.partnerService?.partner?.name ?? "N/A"}
                      </div>
                      <div className="text-gray-500">Mode</div>
                      <div className="text-right text-gray-800">
                        {selectedTransaction.customer?.mode ?? "N/A"}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 md:col-span-2">
                    <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="pi pi-wallet" />
                      Amounts
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="rounded border p-3">
                        <div className="text-xs text-gray-500">Amount</div>
                        <div className="text-base font-semibold text-gray-800">
                          {selectedTransaction.amount} {selectedTransaction.currency}
                        </div>
                      </div>
                      <div className="rounded border p-3">
                        <div className="text-xs text-gray-500">Commission</div>
                        <div className="text-base font-semibold text-gray-800">
                          {selectedTransaction.commission} {selectedTransaction.currency}
                        </div>
                      </div>
                      <div className="rounded border p-3">
                        <div className="text-xs text-gray-500">Balance</div>
                        <div className="text-base font-semibold text-gray-800">
                          {selectedTransaction.balance ?? "N/A"} {selectedTransaction.currency}
                        </div>
                      </div>
                      <div className="rounded border p-3">
                        <div className="text-xs text-gray-500">Status</div>
                        <div className="text-base font-semibold text-gray-800">
                          {selectedTransaction.status}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 md:col-span-2">
                    <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="pi pi-chart-line" />
                      Financial Summary
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      {[
                        ["Amount", selectedTransaction.financialSummary?.amount],
                        ["Commission", selectedTransaction.financialSummary?.commission],
                        ["Provider Fee", selectedTransaction.financialSummary?.providerFee],
                        ["Provider Fee Rate", selectedTransaction.financialSummary?.providerFeeRate],
                        ["Our Margin", selectedTransaction.financialSummary?.ourMargin],
                        ["Enterprise Rate", selectedTransaction.financialSummary?.entrepiseRate],
                        ["Partner Rate", selectedTransaction.financialSummary?.partnerRate],
                        ["Custom Rate", selectedTransaction.financialSummary?.customRate],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded border p-3">
                          <div className="text-xs text-gray-500">{label}</div>
                          <div className="text-base font-semibold text-gray-800">{value ?? "N/A"}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500">
                <Loader />
              </p>
            )}
          </Dialog>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
